// La caja de escribir a Santi (propuesta §5.1, P1/P4): lo único que firma es «Enviar». En un contrato que te toca,
// enviar le pasa el turno a Santi (la oficina deriva lo que le toca); la pista bajo la caja lo dice antes de enviar y deja
// el caso raro a un clic: «Al enviar, le toca a Santi · No, sigo yo» ↔ «Al enviar, te sigue tocando a ti · Que le toque a
// Santi» (vuelve a lo de siempre tras cada envío). Si espera a Santi, o en el chat, no hay pista: escribir no cambia nada.
// Enter envía, Mayús+Enter salta de línea; el borrador se guarda por hilo en el navegador (borradores.ts). Un envío que
// falló sin saber si llegó (5xx, red) se reintenta con la MISMA clave: el centro no lo repite; un «no» del servidor
// (4xx) termina aquel envío y el siguiente es otro mensaje con otra clave.
import { Loader2, SendHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { ApiHttpError } from '../../../lib/api';
import { shouldSendOnEnter } from '../../../lib/composer-keys';
import { MD_QUERY, useMediaQuery } from '../../../hooks/useMediaQuery';
import { guardarBorrador, useBorrador } from '../chat/borradores';
import { envioTermino, mensajeErrorPelotas } from '../errores';
import { Boton } from '../ui';
import { LIMITES, pelotasApi } from './api';
import { Contador } from './piezas';
import { pedirHilo, usePelotasStore } from './store';

export const claveBorradorSanti = (hilo: number | 'general') => `santi:${hilo}`;

/** El mensaje de un fallo: si no se sabe si llegó, el de la propuesta; si el servidor dijo que no, su motivo. */
export function errorDeEnvio(e: unknown): string {
  return envioTermino(e) ? mensajeErrorPelotas(e) : t('oficinaV2.pelotas.chat.noSePudo');
}

/** El servidor no aceptó los datos (400 DATOS_INVALIDOS): se dice qué, en palabras, si se sabe. */
export const datosInvalidos = (e: unknown): boolean => e instanceof ApiHttpError && e.code === 'DATOS_INVALIDOS';

const mensajeLargo = (texto: string): string | null => (texto.trim().length > LIMITES.texto ? t('oficinaV2.pelotas.chat.textoLargo', { max: LIMITES.texto }) : null);

export function CompositorSanti({
  hilo,
  conTurno,
  corta = false,
  relleno,
  alRellenado,
}: {
  hilo: number | 'general';
  /** Le toca a Pau en este contrato: enviar le pasa el turno a Santi (y sale la pista). */
  conTurno: boolean;
  /** El cajón del agente empuja la caja: la pista en corto («Le toca a Santi · No, sigo yo»). */
  corta?: boolean;
  /** Lo que «Usar en el chat» dejó en la caja (se aplica una vez por cambio). */
  relleno?: { n: number; texto: string } | null;
  /** Ya está en la caja (y en su borrador): quien lo dejó lo suelta, para no volver a ponerlo al volver a este hilo. */
  alRellenado?: () => void;
}) {
  const [texto, setTexto] = useBorrador(claveBorradorSanti(hilo));
  const [seguirYo, setSeguirYo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clave = useRef<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const aplicado = useRef(0);
  const dedo = useMediaQuery('(pointer: coarse)');
  const anchoEscritorio = useMediaQuery(MD_QUERY);
  const enterSaltaLinea = dedo && !anchoEscritorio;

  useEffect(() => {
    if (!relleno || relleno.n === aplicado.current) return;
    aplicado.current = relleno.n;
    setTexto(relleno.texto);
    area.current?.focus();
    alRellenado?.();
  }, [relleno, setTexto, alRellenado]);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [texto]);

  async function enviar() {
    // Sin nada que mandar, Enviar solo devuelve el foco a la caja (nunca un error).
    if (!texto.trim()) {
      area.current?.focus();
      return;
    }
    if (enviando) return;
    // Un borrador de antes puede pasar del límite (la caja ya no deja escribir más): se dice antes de enviar.
    const largo = mensajeLargo(texto);
    if (largo) {
      setError(largo);
      area.current?.focus();
      return;
    }
    setEnviando(true);
    setError(null);
    clave.current ??= crypto.randomUUID();
    try {
      await pelotasApi.mensaje(hilo, { texto: texto.trim(), seguirYo: conTurno && seguirYo }, clave.current);
      clave.current = null;
      setTexto('');
      guardarBorrador(claveBorradorSanti(hilo), '');
      setSeguirYo(false);
      pedirHilo(hilo);
      void usePelotasStore.getState().cargarLista();
      area.current?.focus();
    } catch (e) {
      if (envioTermino(e)) clave.current = null;
      setError((datosInvalidos(e) && mensajeLargo(texto)) || errorDeEnvio(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <footer className="shrink-0 border-t border-oficina-borde bg-oficina-panel px-6 pb-4 pt-3" data-testid="compositor-santi">
      {error ? (
        <p role="alert" className="mb-2 px-1 text-[14px] leading-5 text-oficina-error">
          {error}
        </p>
      ) : null}
      <div className="rounded-2xl bg-oficina-fondo ring-1 ring-inset ring-oficina-linea transition-colors focus-within:ring-white/20">
        <textarea
          ref={area}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (!e.nativeEvent.isComposing && shouldSendOnEnter(e, { isCoarsePointer: enterSaltaLinea })) {
              e.preventDefault();
              void enviar();
            }
          }}
          maxLength={LIMITES.texto}
          placeholder={t('oficinaV2.pelotas.chat.escribe')}
          aria-label={t('oficinaV2.pelotas.chat.escribe')}
          className="block max-h-[200px] min-h-[52px] w-full resize-none bg-transparent px-4 pb-1 pt-3.5 text-[16px] leading-6 text-oficina-texto outline-none placeholder:text-oficina-texto3"
          data-testid="compositor-texto"
        />
        <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1">
          <span className="flex-1 pl-2.5">
            <Contador n={texto.length} max={LIMITES.texto} />
          </span>
          {conTurno ? (
            <span className="mr-1 inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[14px] leading-5 text-oficina-texto3" data-testid="compositor-pista">
              {t(seguirYo ? (corta ? 'oficinaV2.pelotas.chat.pistaSiguesCorta' : 'oficinaV2.pelotas.chat.pistaSigues') : corta ? 'oficinaV2.pelotas.chat.pistaLeTocaCorta' : 'oficinaV2.pelotas.chat.pistaLeToca')} ·
              <button
                type="button"
                onClick={() => setSeguirYo((v) => !v)}
                aria-pressed={seguirYo}
                className="inline-flex h-11 items-center rounded font-medium text-oficina-texto2 underline decoration-oficina-texto2/45 underline-offset-[3px] outline-none hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-white/85"
                data-testid="compositor-seguir-yo"
              >
                {t(seguirYo ? 'oficinaV2.pelotas.chat.queLeToque' : 'oficinaV2.pelotas.chat.noSigoYo')}
              </button>
            </span>
          ) : null}
          <Boton variante="primario" className="h-11 rounded-[10px] px-5 text-[16px]" aria-disabled={!texto.trim()} onClick={() => void enviar()} data-testid="compositor-enviar">
            {enviando ? <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden /> : <SendHorizontal className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden />}
            {t('oficinaV2.pelotas.chat.enviar')}
          </Boton>
        </div>
      </div>
    </footer>
  );
}
